/**
 * Month helpers for the year plan. Planning happens in whole months, so every
 * value here is a `YYYY-MM` string – which also sorts and compares correctly.
 */

export const monthKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const yearOf = (month: string): number => Number(month.slice(0, 4));

/** `2026` → `['2026-01', … '2026-12']`. */
export const monthsOfYear = (year: number): string[] =>
  Array.from({ length: 12 }, (_, index) => `${year}-${String(index + 1).padStart(2, '0')}`);

/** Months since the epoch – lets month arithmetic work without Date objects. */
const absolute = (month: string): number => {
  const [year, index] = month.split('-').map(Number) as [number, number];
  return year * 12 + (index - 1);
};

const fromAbsolute = (value: number): string =>
  `${Math.floor(value / 12)}-${String((value % 12) + 1).padStart(2, '0')}`;

export const addMonths = (month: string, delta: number): string =>
  fromAbsolute(absolute(month) + delta);

/** Whole months from `from` to `to` (negative when `to` is earlier). */
export const monthsBetween = (from: string, to: string): number =>
  absolute(to) - absolute(from);

/** Inclusive on both ends; an investment with only a first month covers that one month. */
export function coversMonth(
  month: string,
  plannedFrom: string | null,
  plannedTo: string | null,
): boolean {
  if (!plannedFrom) return false;
  return month >= plannedFrom && month <= (plannedTo ?? plannedFrom);
}

/** Column headers of the year grid. */
export const MONTH_INITIALS = [
  'J',
  'F',
  'M',
  'A',
  'M',
  'J',
  'J',
  'A',
  'S',
  'O',
  'N',
  'D',
] as const;
